import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalRegisterParkingDetailComponent } from '../../modals/parking/register-parking/modal-register-parking-detail/modal-register-parking-detail.component';
import { ModalRegisterParkingFormComponent } from '../../modals/parking/register-parking/modal-register-parking-form/modal-register-parking-form.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';
import { ApiService } from '../../../../core/services/api.service';
import { CityParkingRegisterGroupResponse } from '../../../../core/models/register-parking';
import {
  RegisterParkingChartItem,
  RegisterParkingDashboardStat,
  RegisterParkingRecentActivityView,
  RegisterParkingTableDto,
} from '../../../../core/models/parking/register-parking-list.model';
import { MapperRegisterParkingListService } from '../../../../core/mappers/parking/mapper-register-parking-list.service';
import { ApiRegisterParkingListService } from '../../../../core/services/api/parking/api-register-parking-list.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ClientAccessScopeService } from '../../../../core/services/client-access-scope.service';
import { ClientIInfo } from '../../../../core/models/parking.model';

@Component({
  selector: 'app-register-parking',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    ModalRegisterParkingDetailComponent,
    ModalRegisterParkingFormComponent,
    ModalInvoicePdfViewerComponent,
  ],
  templateUrl: './register-parking.component.html',
  styleUrl: './register-parking.component.css',
  providers: [DatePipe],
})
export class RegisterParkingComponent implements OnInit {

  readonly siteState = inject(SiteStateService);
  readonly clientScope = inject(ClientAccessScopeService);
  private readonly apiRegisterParkingListService = inject(ApiRegisterParkingListService);
  private readonly apiService = inject(ApiService);
  private readonly mapper = inject(MapperRegisterParkingListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly datePipe = inject(DatePipe);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  registerParkings: CityParkingRegisterGroupResponse[] = [];
  protected Math = Math;

  searchText = '';
  searchType: 'customerName' | 'name' | 'invoiceNo' | 'description' | 'clientName' = 'customerName';
  filterClientId = 0;
  clientOptions: ClientIInfo[] = [];
  isSearchTypeDropdownOpen = false;
  filterStatus = 0;
  minFee: number | null = null;

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  /** yyyy-MM-dd — same pattern as transaction-parking */
  startDate = '';
  endDate = '';

  isLoading = false;
  isDashboardLoading = false;
  showDashboard = true;
  dashboardStats: RegisterParkingDashboardStat[] = [];
  recentActivity: RegisterParkingRecentActivityView[] = [];
  dashboardData: RegisterParkingChartItem[] = [];

  chartMode: 'daily' | 'monthly' = 'daily';
  dailySubMode: 'default' | 'custom' = 'default';
  chartType: 'line' | 'bar' = 'line';
  chartWidth = '100%';
  customStartDate = '';
  customEndDate = '';
  selectedMonthStart = new Date().toISOString().slice(0, 7);
  selectedMonthEnd = new Date().toISOString().slice(0, 7);
  yAxisLabels: number[] = [];
  chartTotalCount = 0;
  currentMonthPoints = '';
  currentMonthAreaPoints = '';
  currentMonthDots: Array<{ x: number; y: number; label: string; subLabel: string; value: number; color: string }> = [];

  paymentAmount = 0;
  isModalPaymentOpen = false;
  isModalDetailOpen = false;
  isFormModalOpen = false;
  selectedRegisterParkingId = 0;
  editGroupId = 0;
  userId = 0;

  showPdfModal = false;
  pdfSrc: Uint8Array | null = null;
  isProcessingPdf = false;
  pdfRegisterGroupId = 0;

  constructor() {
    this.applyDefaultDateRange();

    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;

      untracked(() => {
        this.currentPage = 1;
        this.loadScopeAndData();
      });
    });
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.loadScopeAndData();
    }
  }

  get isClientFilterLocked(): boolean {
    return this.clientScope.isClientLocked();
  }

  private resolveClientFilterId(): number | undefined {
    if (this.filterClientId > 0) {
      return this.filterClientId;
    }
    const locked = this.clientScope.lockedClientId();
    return locked > 0 ? locked : undefined;
  }

  loadScopeAndData(forceScope = false): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.clientOptions = [];
      this.registerParkings = [];
      this.resetDashboardView();
      return;
    }

    this.clientScope.load(siteId, this.userId, forceScope).subscribe({
      next: (state) => {
        this.clientOptions = state.clients;
        if (this.clientScope.isClientLocked() && state.clients.length === 1) {
          this.filterClientId = state.clients[0].id;
        }
        this.loadTable();
        this.loadMarketingDashboard();
      },
      error: () => {
        this.loadTable();
        this.loadMarketingDashboard();
      },
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get menuId(): number {
    return this.permissionService.getMenuId(this.router.url) ?? 0;
  }

  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  formatDate(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  getTimeAgo(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '-';
    const date = new Date(dateInput);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' ปีที่แล้ว';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' เดือนที่แล้ว';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' วันที่แล้ว';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' ชั่วโมงที่แล้ว';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' นาทีที่แล้ว';
    return 'เพิ่งสร้าง';
  }

  get filteredAndPaginatedData(): CityParkingRegisterGroupResponse[] {
    return this.registerParkings;
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize = Number(element.value);
    this.currentPage = 1;
    this.loadTable();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onMinFeeChange(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onDateChange(): void {
    if ((this.startDate && !this.endDate) || (!this.startDate && this.endDate)) {
      return;
    }
    this.currentPage = 1;
    this.loadTable();
    this.loadMarketingDashboard();
  }

  clearDateFilter(): void {
    this.applyDefaultDateRange();
    this.currentPage = 1;
    this.loadTable();
    this.loadMarketingDashboard();
  }

  private applyDefaultDateRange(): void {
    const today = this.formatDate(new Date());
    this.startDate = today;
    this.endDate = today;
  }

  get tableDateRangeLabel(): string {
    if (this.startDate && this.endDate) {
      return `${this.formatDisplayDate(this.startDate)} - ${this.formatDisplayDate(this.endDate)}`;
    }
    return 'ทั้งหมด';
  }

  formatDisplayDate(value: string): string {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }

  onRefresh(): void {
    this.searchText = '';
    this.searchType = 'customerName';
    this.filterStatus = 0;
    this.minFee = null;
    if (!this.isClientFilterLocked) {
      this.filterClientId = 0;
    }
    this.applyDefaultDateRange();
    this.currentPage = 1;
    this.loadScopeAndData(true);
  }

  selectStatus(status: number): void {
    this.filterStatus = this.filterStatus === status ? 0 : status;
    this.currentPage = 1;
    this.loadTable();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'customerName' | 'name' | 'invoiceNo' | 'description' | 'clientName'): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'customerName': return 'ชื่อลูกค้า';
      case 'name': return 'ชื่อรายการ';
      case 'invoiceNo': return 'เลขที่ใบแจ้งหนี้';
      case 'description': return 'หมายเหตุ';
      case 'clientName': return 'บริษัทผู้เช่า';
      default: return 'ค้นหา';
    }
  }

  onClientFilterChange(): void {
    this.currentPage = 1;
    this.loadTable();
    this.loadMarketingDashboard();
  }

  navigateToCreate(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือก Site จาก Sidebar ก่อน');
      return;
    }
    this.editGroupId = 0;
    this.isFormModalOpen = true;
  }

  openEditForm(id: number): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือก Site จาก Sidebar ก่อน');
      return;
    }
    this.editGroupId = id;
    this.isFormModalOpen = true;
  }

  closeFormModal(): void {
    this.isFormModalOpen = false;
    this.editGroupId = 0;
  }

  onFormSaved(): void {
    this.isFormModalOpen = false;
    this.editGroupId = 0;
    this.loadScopeAndData();
  }

  navigateToPayment(): void {
    this.isModalPaymentOpen = true;
  }

  onConfirmModalPayment(amount: number): void {
    this.isModalPaymentOpen = false;
    this.paymentAmount = amount;
    this.toast.success(`บันทึกการชำระเงิน: ${amount} บาท`);
  }

  onCloseModalPayment(): void {
    this.isModalPaymentOpen = false;
    this.loadTable();
  }

  confirmPaymentForCustomer(customer: CityParkingRegisterGroupResponse): void {
    this.navigateToPayment();
    this.registerParkings = this.registerParkings.map(c =>
      c.id === customer.id ? { ...c, fee: this.paymentAmount } : c
    );
  }

  openDetailModal(id: number): void {
    this.selectedRegisterParkingId = id;
    this.isModalDetailOpen = true;
  }

  closeDetailModal(): void {
    this.isModalDetailOpen = false;
    this.selectedRegisterParkingId = 0;
  }

  saveDetailModal(): void {
    this.isModalDetailOpen = false;
    this.selectedRegisterParkingId = 0;
    this.loadScopeAndData();
  }

  printRegisterParkingPdf(registerGroupId: number, event?: Event): void {
    event?.stopPropagation();
    if (!registerGroupId) return;

    this.isProcessingPdf = true;
    this.pdfRegisterGroupId = registerGroupId;
    this.apiService.getRegisterParkingPdf(registerGroupId).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal = true;
        this.isProcessingPdf = false;
      },
      error: (err) => {
        console.error('[API Error] getRegisterParkingPdf:', err);
        this.toast.error('ไม่สามารถดึงไฟล์ PDF รายงานทะเบียนสมาชิกได้');
        this.isProcessingPdf = false;
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal = false;
    this.pdfSrc = null;
    this.pdfRegisterGroupId = 0;
  }

  setChartMode(mode: 'daily' | 'monthly'): void {
    this.chartMode = mode;
    if (mode === 'monthly') {
      const today = new Date();
      this.selectedMonthStart = today.toISOString().slice(0, 7);
      this.selectedMonthEnd = today.toISOString().slice(0, 7);
      this.onChartMonthChange();
      return;
    }
    this.processDashboardData();
  }

  setChartType(type: 'line' | 'bar'): void {
    this.chartType = type;
  }

  onChartMonthChange(): void {
    if (this.chartMode !== 'monthly') return;
    this.processDashboardData();
  }

  setDailySubMode(mode: 'default' | 'custom'): void {
    this.dailySubMode = mode;
    if (mode === 'custom') {
      if (!this.customStartDate) {
        const e = new Date();
        const s = new Date();
        s.setDate(e.getDate() - 9);
        this.customEndDate = this.formatDate(e);
        this.customStartDate = this.formatDate(s);
      }
      this.onCustomDateChange();
    } else {
      this.processDashboardData();
    }
  }

  onCustomDateChange(): void {
    if (!this.customStartDate || !this.customEndDate) return;
    const s = new Date(this.customStartDate);
    const e = new Date(this.customEndDate);
    if (e < s) this.customEndDate = this.customStartDate;

    const diffDays = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 10) {
      this.toast.warn('แสดงผลได้สูงสุด 10 วัน');
      const newEnd = new Date(s);
      newEnd.setDate(s.getDate() + 9);
      this.customEndDate = this.formatDate(newEnd);
    }
    this.processDashboardData();
  }

  get maxCustomDate(): string {
    if (!this.customStartDate) return '';
    const d = new Date(this.customStartDate);
    d.setDate(d.getDate() + 10);
    return this.formatDate(d);
  }

  resetCustomDate(): void {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - 9);
    this.customEndDate = this.formatDate(e);
    this.customStartDate = this.formatDate(s);
    this.processDashboardData();
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.registerParkings = [];
      this.totalItems = 0;
      this.totalPages = 1;
      return;
    }

    this.isLoading = true;
    const status = this.mapper.mapStatusFilter(this.filterStatus);

    this.apiRegisterParkingListService.getRegisterParkingForTable({
      siteId,
      userId: this.userId,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField: this.searchText.trim() ? this.mapper.mapSearchField(this.searchType) : undefined,
      searchText: this.searchText.trim() || undefined,
      status: status ?? 'all',
      startDate: this.startDate || undefined,
      endDate: this.endDate || undefined,
      minFee: this.minFee && this.minFee > 0 ? this.minFee : undefined,
      clientId: this.resolveClientFilterId(),
    }).subscribe({
      next: (res: RegisterParkingTableDto) => {
        const mapped = this.mapper.mapTableFromDto(res);
        this.registerParkings = mapped.items;
        this.totalItems = mapped.total;
        this.totalPages = mapped.totalPages || Math.ceil(mapped.total / (this.pageSize || 1));
        this.isLoading = false;
      },
      error: (err) => {
        console.error('[API Error] getRegisterParkingForTable:', err);
        this.registerParkings = [];
        this.totalItems = 0;
        this.totalPages = 1;
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดข้อมูลลงทะเบียนสมาชิกได้');
      },
    });
  }

  loadMarketingDashboard(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.resetDashboardView();
      return;
    }

    this.isDashboardLoading = true;
    this.apiRegisterParkingListService.getRegisterParkingMarketingDashboard(
      siteId,
      this.userId,
      this.startDate || undefined,
      this.endDate || undefined,
      this.resolveClientFilterId()
    ).subscribe({
      next: (res) => {
        this.applyMarketingDashboard(this.mapper.mapMarketingDashboard(res));
        this.isDashboardLoading = false;
      },
      error: (err) => {
        console.error('[API Error] getRegisterParkingMarketingDashboard:', err);
        this.resetDashboardView();
        this.isDashboardLoading = false;
      },
    });
  }

  private applyMarketingDashboard(view: ReturnType<MapperRegisterParkingListService['mapMarketingDashboard']>): void {
    this.dashboardStats = view.stats;
    this.dashboardData = view.chartItems;
    this.recentActivity = view.recentActivity.map(item => ({
      initial: item.customerName ? item.customerName.charAt(0).toUpperCase() : '?',
      name: item.customerName,
      timeAgo: this.getTimeAgo(item.createDate ?? null),
      info: `${item.name} (${(item.fee ?? 0).toLocaleString()} ฿)`,
      color: this.getActivityColor(item.id),
    }));
    this.processDashboardData();
  }

  private resetDashboardView(): void {
    this.dashboardStats = [];
    this.recentActivity = [];
    this.dashboardData = [];
    this.chartTotalCount = 0;
    this.currentMonthPoints = '';
    this.currentMonthAreaPoints = '';
    this.currentMonthDots = [];
  }

  processDashboardData(): void {
    let xLabels: Date[] = [];
    let dataPoints: number[] = [];
    let diffDays = 0;

    if (this.chartMode === 'daily') {
      let start: Date;
      let end: Date;
      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = new Date(this.customStartDate);
        end = new Date(this.customEndDate);
      } else {
        start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        end = new Date();
      }

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        xLabels.push(new Date(d));
        dataPoints.push(0);
      }

      this.dashboardData.forEach(item => {
        if (!item.createDate) return;
        const d = new Date(item.createDate);
        const dDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

        if (dDateOnly >= startDateOnly && dDateOnly <= endDateOnly) {
          const idx = xLabels.findIndex(x => x.toDateString() === dDateOnly.toDateString());
          if (idx !== -1) dataPoints[idx]++;
        }
      });
    } else {
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, 1);
      const end = new Date(endYear, endMonth, 0);

      const diffTime = Math.abs(end.getTime() - start.getTime());
      diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 60) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          xLabels.push(new Date(d));
          dataPoints.push(0);
        }
        this.dashboardData.forEach(item => {
          if (!item.createDate) return;
          const d = new Date(item.createDate);
          if (d >= start && d <= end) {
            const idx = xLabels.findIndex(x => x.toDateString() === d.toDateString());
            if (idx !== -1) dataPoints[idx]++;
          }
        });
      } else {
        let temp = new Date(startYear, startMonth - 1, 1);
        while (temp <= end) {
          xLabels.push(new Date(temp));
          dataPoints.push(0);
          temp.setMonth(temp.getMonth() + 1);
        }
        this.dashboardData.forEach(item => {
          if (!item.createDate) return;
          const d = new Date(item.createDate);
          if (d < start || d > end) return;
          const idx = xLabels.findIndex(x => x.getFullYear() === d.getFullYear() && x.getMonth() === d.getMonth());
          if (idx !== -1) dataPoints[idx]++;
        });
      }
    }

    this.chartTotalCount = dataPoints.reduce((a, b) => a + b, 0);

    const rawMax = Math.max(...dataPoints, 5);
    const maxVal = Math.ceil(rawMax / 4) * 4;
    this.yAxisLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal];

    const getCoordinate = (val: number, index: number, total: number) => {
      if (total <= 1) return { x: 50, y: 100 - ((val / maxVal) * 100) };
      const x = 2 + (index / (total - 1)) * 96;
      const y = 100 - ((val / maxVal) * 100);
      return { x, y };
    };

    const pointsObj = dataPoints.map((val, i) => getCoordinate(val, i, dataPoints.length));
    this.currentMonthPoints = pointsObj.map(p => `${p.x},${p.y}`).join(' ');

    const getThaiMonth = (monthIndex: number) => {
      const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
      return months[monthIndex];
    };

    this.currentMonthDots = pointsObj.map((p, i) => {
      const date = xLabels[i];
      let label = `${date.getDate()}`;
      let subLabel = '';

      if (date.getDate() === 1 || i === 0) {
        subLabel = getThaiMonth(date.getMonth());
      }

      if (this.chartMode === 'monthly' && dataPoints.length <= 12 && diffDays > 60) {
        label = getThaiMonth(date.getMonth());
        subLabel = `${date.getFullYear() + 543}`.slice(2);
      }

      return {
        x: p.x,
        y: p.y,
        label,
        subLabel,
        value: dataPoints[i],
        color: this.getChartColor(i),
      };
    });

    if (pointsObj.length > 0) {
      this.currentMonthAreaPoints = `
          ${pointsObj[0].x},100
          ${this.currentMonthPoints}
          ${pointsObj[pointsObj.length - 1].x},100
       `;
    } else {
      this.currentMonthAreaPoints = '';
    }

    this.chartWidth = dataPoints.length > 30 ? `${dataPoints.length * 40}px` : '100%';
  }

  private getActivityColor(id: number): string {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length];
  }

  private getChartColor(index: number): string {
    const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#f87171', '#818cf8', '#a78bfa', '#f472b6'];
    return colors[index % colors.length];
  }
}
